// Subscription and Feature Management Types
export type SubscriptionLevel = 'free' | 'basic' | 'standard' | 'pro';

export interface FeatureLimits {
  customers?: number;
  invoices?: number;
  templates?: number;
  storage?: number; // in MB
}

export interface Feature {
  id: string;
  name: string;
  description: string;
  requiredLevel: SubscriptionLevel;
  category: 'core' | 'advanced' | 'premium';
  limits?: FeatureLimits;
}

export interface FeatureAccess {
  featureId: string;
  hasAccess: boolean;
  requiredLevel: SubscriptionLevel;
  userLevel: SubscriptionLevel;
  limits?: FeatureLimits;
}

// Feature definitions with required subscription levels
export const FEATURES: Record<string, Feature> = {
  // Core features (available to all)
  'dashboard': {
    id: 'dashboard',
    name: 'Dashboard',
    description: 'Basic dashboard access',
    requiredLevel: 'free',
    category: 'core'
  },
  'invoice-basic': {
    id: 'invoice-basic',
    name: 'Basic Invoice Creation',
    description: 'Create and manage basic invoices',
    requiredLevel: 'free',
    category: 'core'
  },

  // Advanced features (Basic+)
  'invoice-templates': {
    id: 'invoice-templates',
    name: 'Invoice Templates',
    description: 'Custom invoice templates and designs',
    requiredLevel: 'basic',
    category: 'advanced'
  },
  'customer-management': {
    id: 'customer-management',
    name: 'Customer Management',
    description: 'Advanced customer database and management',
    requiredLevel: 'free',
    category: 'core',
    limits: {
      customers: 5
    }
  },

  // Premium features (Standard+)
  'multi-company': {
    id: 'multi-company',
    name: 'Multi-Company Support',
    description: 'Manage multiple companies',
    requiredLevel: 'standard',
    category: 'premium'
  },
  'advanced-reporting': {
    id: 'advanced-reporting',
    name: 'Advanced Reporting',
    description: 'Detailed analytics and reporting',
    requiredLevel: 'standard',
    category: 'premium'
  },

  // Pro features (Pro only)
  'template-designer': {
    id: 'template-designer',
    name: 'Template Designer',
    description: 'Advanced template designer for creating custom invoice templates',
    requiredLevel: 'pro',
    category: 'premium'
  },
  'api-access': {
    id: 'api-access',
    name: 'API Access',
    description: 'REST API access for integrations',
    requiredLevel: 'pro',
    category: 'premium'
  },
  'white-label': {
    id: 'white-label',
    name: 'White Label',
    description: 'Remove branding and customize appearance',
    requiredLevel: 'pro',
    category: 'premium'
  },
  'unlimited-storage': {
    id: 'unlimited-storage',
    name: 'Unlimited Storage',
    description: 'Unlimited file storage',
    requiredLevel: 'pro',
    category: 'premium'
  }
};

export interface SubscriptionLimits {
  customers: number;
  invoices: number;
  templates: number;
  storage: number; // in MB
}

// Subscription level hierarchy (higher number = more features)
export const SUBSCRIPTION_HIERARCHY: Record<SubscriptionLevel, number> = {
  'free': 0,
  'basic': 1,
  'standard': 2,
  'pro': 3
};

// Subscription limits per level
export const SUBSCRIPTION_LIMITS: Record<SubscriptionLevel, SubscriptionLimits> = {
  'free': {
    customers: 5,
    invoices: 10,
    templates: 1,
    storage: 100
  },
  'basic': {
    customers: 50,
    invoices: 100,
    templates: 5,
    storage: 500
  },
  'standard': {
    customers: 200,
    invoices: 500,
    templates: 20,
    storage: 2000
  },
  'pro': {
    customers: -1, // unlimited
    invoices: -1,  // unlimited
    templates: -1, // unlimited
    storage: -1     // unlimited
  }
};

// Helper functions
export function hasFeatureAccess(userLevel: SubscriptionLevel, featureId: string): boolean {
  const feature = FEATURES[featureId];
  if (!feature) return false;

  return SUBSCRIPTION_HIERARCHY[userLevel] >= SUBSCRIPTION_HIERARCHY[feature.requiredLevel];
}

export function getFeatureAccess(userLevel: SubscriptionLevel, featureId: string): FeatureAccess {
  const feature = FEATURES[featureId];
  if (!feature) {
    return {
      featureId,
      hasAccess: false,
      requiredLevel: 'pro',
      userLevel
    };
  }

  return {
    featureId,
    hasAccess: hasFeatureAccess(userLevel, featureId),
    requiredLevel: feature.requiredLevel,
    userLevel
  };
}

export function getAvailableFeatures(userLevel: SubscriptionLevel): Feature[] {
  return Object.values(FEATURES).filter(feature =>
    hasFeatureAccess(userLevel, feature.id)
  );
}

export function getUpgradeRequiredFeatures(userLevel: SubscriptionLevel): Feature[] {
  return Object.values(FEATURES).filter(feature =>
    !hasFeatureAccess(userLevel, feature.id)
  );
}

// Limit helper functions
export function getSubscriptionLimits(userLevel: SubscriptionLevel): SubscriptionLimits {
  return SUBSCRIPTION_LIMITS[userLevel];
}

export function hasReachedLimit(userLevel: SubscriptionLevel, limitType: keyof SubscriptionLimits, currentCount: number): boolean {
  const limits = getSubscriptionLimits(userLevel);
  const limit = limits[limitType];
  
  // -1 means unlimited
  if (limit === -1) return false;
  
  return currentCount >= limit;
}

export function getRemainingLimit(userLevel: SubscriptionLevel, limitType: keyof SubscriptionLimits, currentCount: number): number {
  const limits = getSubscriptionLimits(userLevel);
  const limit = limits[limitType];
  
  // -1 means unlimited
  if (limit === -1) return -1;
  
  return Math.max(0, limit - currentCount);
}

export function getLimitText(userLevel: SubscriptionLevel, limitType: keyof SubscriptionLimits, currentCount: number): string {
  const limits = getSubscriptionLimits(userLevel);
  const limit = limits[limitType];
  
  if (limit === -1) return 'customers.limits.unlimited';
  
  const remaining = getRemainingLimit(userLevel, limitType, currentCount);
  return `customers.limits.usedOfTotal:${currentCount}:${limit}:${remaining}:customers.limits.remaining`;
}